/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";

/**
 * Componente de tabla/listado de entidades reutilizable.
 * 
 * Componente flexible que permite mostrar listas de entidades (edificios, departamentos)
 * con dos modos de visualización: lista tradicional y grid. Incluye funcionalidad para ver detalles
 * de cada entidad al hacer click. Se integra con Microsoft Entra para autenticación.
 */
function EntityTable({ endpoint, title, detailFields = [], renderItem, grid = false, showImage = false }) {
  
  /* ============================
     Autenticación (MSAL)
     ============================ */
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  /* ============================
     Estado principal
     ============================ */
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

   /* ============================
     NUEVO: estado del buscador
     ============================ */

  const [search, setSearch] = useState("");

  /* ============================
     NUEVO: items filtrados
     ============================ */

  const filteredItems = items.filter((item) =>
    (item.nombre || item.title || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

 /* ============================
     Funciones de obtención de datos
     ============================ */

  /**
   * Obtiene la lista de entidades desde la API.
   * Maneja la autenticación con Microsoft Entra y procesa la respuesta.
   */
  const fetchData = async () => {
    if (!isAuthenticated || accounts.length === 0) return;

    setLoading(true);
    try {
      
      // Obtener token de acceso
      const responseToken = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      const token = responseToken.accessToken;

      // Llamada a la API para obtener la lista
      const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Obtener datos (paginados o no)
      const data = res.data.content ? res.data.content : res.data;

      // Ordenar datos por nombre
      const dataOrdenada = data.sort((a, b) =>
        a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
      );

      setItems(dataOrdenada);

    } catch (err) {
      console.error(`Error cargando ${title}:`, err);
      setAlertMessage(`Error cargando ${title}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtiene los detalles de una entidad específica.
   */
  const handleSelect = async (id) => {
    if (!isAuthenticated || accounts.length === 0) return;

    setLoading(true);
    try {
      // Obtener token de acceso
      const responseToken = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_API_SCOPE],
      });
      const token = responseToken.accessToken;

      // Llamada a la API para obtener detalles del item
      const res = await axios.get(`${process.env.REACT_APP_API_URL}${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedItem(res.data);
    } catch (err) {
      console.error("Error cargando detalle:", err);
      setAlertMessage("Error cargando detalle");
    } finally {
      setLoading(false);
    }
  };

  /* ============================
     Efectos
     ============================ */

  /**
   * Carga inicial de datos al montar el componente.
   * Se ejecuta cuando cambian las dependencias críticas.
   */
  useEffect(() => {
    fetchData();
  }, [endpoint, title, isAuthenticated, accounts]);

  /* ============================
     Vista de detalle
     ============================ */
  
  // Si hay un item seleccionado, mostrar vista detalle
  if (selectedItem) {
    // Construir URL de imagen: foto específica o imagen por defecto
    const imageUrl = selectedItem.fotoUrl
      ? `${process.env.REACT_APP_URL}${selectedItem.fotoUrl}`
      : `${process.env.REACT_APP_URL}/uploads/default-building.png`;

    // Estilo para la imagen: efecto blur si es imagen por defecto
    const imageStyle = {
      width: "100%",
      height: "220px",
      backgroundImage: `url("${imageUrl}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      filter: imageUrl.includes('default-building.png') ? 'blur(2px)' : 'none',
    };

    /* ======================================
      Renderizado vista de item seleccionado
     ======================================== */
    return (
      /* Tarjeta de detalle con sombra y bordes redondeados */
      <div
        className="card shadow ms-2 me-2"
        style={{ borderRadius: "16px", overflow: "hidden"}}
      >

        {/* Imagen del edificio/departamento */}
        {showImage && <div style={imageStyle} />}

        {/* Contenido de la tarjeta */}
        <div className="card-body">
          {/* Título del item */}
          <h5 className="card-title mb-3">
            <strong>{selectedItem.nombre || selectedItem.title}</strong>
          </h5>

          {/* Lista de campos detallados */}
          <ul className="list-group mb-3">
            {detailFields.map(({ label, value }) => (
              <li key={label} className="list-group-item">
                <strong>{label}:</strong> {value(selectedItem)}
              </li>
            ))}
          </ul>

          {/* Botón para volver a la lista */}
          <button
            className="btn btn-primary mt-2"
            onClick={() => setSelectedItem(null)}
          >
	  <i className="bi bi-arrow-left me-2"></i>
            Volver a la lista
          </button>
        </div>
      </div>
    );
  }



  /* =====================================
      Renderizado vista de lista de items
     ===================================== */
  return (
    /* Tarjeta principal contenedora */
    <div className="card shadow ms-2 me-2">
      <div className="card-body">
        {/* Título de la sección con línea decorativa */}
        <div className="text-center mb-4">
          <h5 className="card-title fw-semibold mb-2">
            {title}
          </h5>
          <div
            style={{
              width: "140px",
              height: "3px",
              backgroundColor: "#0d6efd",
              margin: "0 auto",
              borderRadius: "2px"
            }}
          />
        </div>

 	{/* ============================
           BUSCADOR
           ============================ */}

        <div className="input-group mb-3">
          <span className="input-group-text">
            <i className="bi bi-search"></i>
          </span>

          <input
            type="text"
            className="form-control"
            placeholder={`Buscar ${title.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            className="btn btn-outline-primary"
            onClick={() => setSearch("")}>
            Reset
          </button>
        </div>


        {/* Contenido condicional según estado */}
        {loading ? (
          // Estado de carga
          <div className="text-center">Cargando...</div>
        ) : items.length === 0 ? (
          // Sin datos
          <div className="alert alert-warning">No hay {title.toLowerCase()} registrados.</div>
        ) : (
          // Lista de items
          <div className="card" style={{ height: "53vh"}}>
            {/* Contenedor con scroll */}
            <div className="card-body flex-grow-1 overflow-auto" style={{backgroundColor: "rgba(78, 77, 77, 0.1)"}}>
              {/* Grid o Lista según prop grid */}
              <div
                className={
                  "row row-cols-1 row-cols-md-2 g-2"
                }
              >
                {filteredItems.map((item) =>
                  grid ? (
                    // Vista Grid
                    <div className="col" key={item.id}>
                      <div
                        className="card h-100 shadow-lg"
                        style={{
                          borderRadius: "18px",
                          overflow: "hidden"
                        }}
                        role="button"
                        onClick={() => handleSelect(item.id)}
                      >
                        {renderItem ? (
                          // Renderizado personalizado
                          renderItem(item)
                        ) : (
                          // Renderizado por defecto
                          <div className="card-body text-center shadow-lg">
                            <h6 className="fw-semibold">{item.nombre || item.title}</h6>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    // Vista Lista
                    <div>
                       <h6 className="fw-semibold">{renderItem ? renderItem(item) : <span>{item.nombre || item.title}</span>}</h6>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mensaje de alerta/error */}
        {alertMessage && <div className="alert alert-danger mt-2">{alertMessage}</div>}
      </div>
    </div>
  );
}

export default EntityTable;
